import { useTranslation } from "react-i18next";
import { Col, Divider, Form, Input, InputNumber, Modal, Row, Select } from "antd";
import { doc, updateDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { FIRESTORE_CUSTOMERS_TABLE } from "../../../../../constants/FirestoreConstant";
import { firestore } from "../../../../../configs/FirebaseConfig";
import dayjs from "dayjs";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import { DEFAULT_CREDIT_LIMIT, PAYMENT_METHODS } from "../../../../../constants/PaymentConstants";
import { CURRENCY } from "../../../../../constants/DeliveryConstants";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { updateExactAccount } from "../../../../../functions/api/exact";
import { showOperationSummary } from "../../../../../utils/operationSummary";
import { COUNTRY_CODE_NETHERLANDS } from "../../../../../constants/OrderConstant";
import { PRODUCT_STOCK_COUNTRIES } from "../../../../../constants/ProductConstant";
import i18n from "../../../../../translations/i18n";

export default function EditPaymentDetailsModal({ company, visible, onClose, priceLists }) {
  const { t } = useTranslation();
  const [paymentDetailsForm] = Form.useForm();
  const { email } = useAuth();
  const { getCountryPricing } = useCountries();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (company && visible) {
      paymentDetailsForm.setFieldsValue({
        paymentCardHolder: company.paymentCardHolder,
        paymentCardNumber: company.paymentCardNumber,
        paymentMethod: company.paymentMethod,
        invoiceEmail: company.invoiceEmail,
        invoiceStreet: company.invoiceStreet,
        invoiceHouseNumber: company.invoiceHouseNumber,
        invoiceHouseNumberAddition: company.invoiceHouseNumberAddition,
        invoiceZipCode: company.invoiceZipCode,
        invoiceCity: company.invoiceCity,
        invoiceCountry: company.invoiceCountry,
        priceListId: company.priceListId || null,
      });
      if (company.creditLimit) paymentDetailsForm.setFieldsValue({ creditLimit: company.creditLimit });
      if (company.customerSnelstartDiscount)
        paymentDetailsForm.setFieldsValue({ customerSnelstartDiscount: company.customerSnelstartDiscount });
    } else {
      paymentDetailsForm.resetFields();
    }
  }, [company, visible]);

  const handleSavePaymentDetails = () => {
    paymentDetailsForm.validateFields().then(async (values) => {
      setIsLoading(true);

      // Initialize operation results
      const operationResults = {
        firestore: { attempted: true, success: false, error: null },
        exact: { attempted: false, success: false, error: null },
      };

      const obj = {
        paymentCardHolder: values.paymentCardHolder || "",
        paymentCardNumber: values.paymentCardNumber || "",
        paymentMethod: values.paymentMethod,
        customerSnelstartDiscount: values.customerSnelstartDiscount || 0,
        invoiceEmail: values.invoiceEmail || null,
        invoiceStreet: values.invoiceStreet || "",
        invoiceHouseNumber: values.invoiceHouseNumber || "",
        invoiceHouseNumberAddition: values.invoiceHouseNumberAddition || "",
        invoiceZipCode: values.invoiceZipCode || "",
        invoiceCity: values.invoiceCity || "",
        invoiceCountry: values.invoiceCountry || "",
        creditLimit:
          values.creditLimit || getCountryPricing(company?.country)?.defaultCreditLimit || DEFAULT_CREDIT_LIMIT,
        priceListId: values.priceListId || null,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };
      // Check if all required address fields are filled in to generate billing address
      if (values.invoiceStreet && values.invoiceHouseNumber && values.invoiceCity && values.invoiceZipCode) {
        obj.customerBillingAddress =
          values.invoiceStreet +
          " " +
          values.invoiceHouseNumber +
          (values.invoiceHouseNumberAddition ? " " + values.invoiceHouseNumberAddition : "") +
          ", " +
          values.invoiceCity +
          ", " +
          values.invoiceZipCode;
      }

      // Update Firestore first
      try {
        await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, company.id), obj);

        operationResults.firestore.success = true;

        // If customer has exactId, also update Exact
        if (company.exactId) {
          operationResults.exact.attempted = true;
          try {
            const token = await getFirebaseIDToken();
            const exactUpdateObj = {
              exactAccountId: company.exactId,
              paymentMethod: values.paymentMethod,
              customerName: company.customerName,
              paymentCardHolder: values.paymentCardHolder,
              paymentCardNumber: values.paymentCardNumber,
              invoiceStreet: values.invoiceStreet,
              invoiceHouseNumber: values.invoiceHouseNumber,
              invoiceHouseNumberAddition: values.invoiceHouseNumberAddition,
              invoiceCity: values.invoiceCity,
              invoiceZipCode: values.invoiceZipCode,
              invoiceCountry: values.invoiceCountry || company.country || COUNTRY_CODE_NETHERLANDS,
              customerDiscount: values.customerSnelstartDiscount,
              creditLimit:
                values.creditLimit || getCountryPricing(company?.country)?.defaultCreditLimit || DEFAULT_CREDIT_LIMIT,
            };

            if (!!values.invoiceEmail) {
              exactUpdateObj.invoiceEmail = values.invoiceEmail;
            }

            await updateExactAccount(exactUpdateObj, token);
            operationResults.exact.success = true;
          } catch (exactError) {
            console.error("Exact update error:", exactError);
            // Extract the most detailed error message available
            const detailedError =
              exactError.response?.data?.Message || exactError.response?.data?.message || exactError.message;
            operationResults.exact.error = detailedError;
          }
        }

        setIsLoading(false);
        onClose();
        showOperationSummary(operationResults, t);
      } catch (firestoreError) {
        console.error("Firestore error:", firestoreError);
        operationResults.firestore.error = firestoreError.message;
        setIsLoading(false);
        showOperationSummary(operationResults, t);
      }
    });
  };

  return (
    <Modal
      title={t("edit_payment_details_value", { company: !!company ? company.customerName : "" })}
      open={visible}
      onOk={handleSavePaymentDetails}
      onCancel={onClose}
      okText={t("save")}
      cancelText={t("close")}
      okButtonProps={{ loading: isLoading, htmlType: "submit" }}
      zIndex={1002}
      centered
      width={800}
    >
      <Form form={paymentDetailsForm} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item name="paymentCardNumber" label={t("payment_card_number")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="paymentCardHolder" label={t("payment_card_holder")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name={"customerSnelstartDiscount"} label={t("customer_discount")} initialValue={0}>
              <InputNumber
                placeholder={t("customer_discount")}
                style={{ width: "100%" }}
                min={0}
                max={100}
                formatter={(value) => `${value || 0}%`}
                parser={(value) => (value === null || value === void 0 ? void 0 : value.replace("%", ""))}
              />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              name={"creditLimit"}
              label={t("credit_limit")}
              initialValue={getCountryPricing(company?.country)?.defaultCreditLimit ?? DEFAULT_CREDIT_LIMIT}
            >
              <InputNumber
                placeholder={t("credit_limit")}
                style={{ width: "100%" }}
                step={50}
                min={0}
                formatter={(value) => `${CURRENCY} ${value || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => (value === null || value === void 0 ? void 0 : value.replace(/\$\s?|(,*)/g, ""))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="paymentMethod" label={t("payment_method")}>
              <Select style={{ width: "100%" }} placeholder={t("select_payment_method")}>
                {PAYMENT_METHODS.map((method) => (
                  <Select.Option key={method} value={method}>
                    {t(method.toLowerCase().replace(" ", "_"))}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="invoiceEmail" label={t("invoice_email")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="priceListId" label={t("price_list")}>
              <Select allowClear placeholder={t("form.select_price_list")}>
                {priceLists &&
                  priceLists.map((priceList) => (
                    <Select.Option key={priceList.id} value={priceList.id}>
                      {priceList.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ margin: "0 0 12px 0" }} />
        <Row gutter={16}>
          <Col xs={24}>
            <h4>{t("invoice_address")}</h4>
          </Col>
          <Col xs={24}>
            <Form.Item name="invoiceStreet" label={t("street")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="invoiceZipCode" label={t("zip_code")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} lg={6}>
            <Form.Item name="invoiceHouseNumber" label={t("house_number")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} lg={6}>
            <Form.Item name="invoiceHouseNumberAddition" label={t("addition")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="invoiceCity" label={t("city")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="invoiceCountry" label={t("country")}>
              <Select className="mr-2">
                {PRODUCT_STOCK_COUNTRIES.map((country) => (
                  <Select.Option key={country.code} value={country.code}>
                    {i18n.t(country.label)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
